"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, Clock3, LoaderCircle, Ruler } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import type { CatalogBreed } from "@/lib/breed-catalog";
import { getBreedIllustration } from "@/lib/breed-illustrations";
import { getBreedSafetyLabel } from "@/lib/breed-safety";

type BreedPage = { items: CatalogBreed[]; total: number; page: number; limit: number; hasMore: boolean };
function guideOf(breed: CatalogBreed) {
  if (!breed.guide) return {};
  if (typeof breed.guide !== "string") return breed.guide;
  try { return JSON.parse(breed.guide) as Exclude<CatalogBreed["guide"], string | null>; } catch { return {}; }
}

export function BreedDirectory({ initialPage, query, initial, initials }: { initialPage: BreedPage; query: string; initial: string; initials: readonly string[] }) {
  const [items, setItems] = useState(initialPage.items);
  const [total, setTotal] = useState(initialPage.total);
  const [activeInitial, setActiveInitial] = useState(initial);
  const [page, setPage] = useState(initialPage.page);
  const [hasMore, setHasMore] = useState(initialPage.hasMore);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const sentinel = useRef<HTMLDivElement>(null);

  const loadNext = useCallback(async () => {
    if (!hasMore || loading) return;
    setLoading(true);
    setFailed(false);
    const params = new URLSearchParams({ page: String(page + 1), limit: String(initialPage.limit) });
    if (query) params.set("q", query);
    if (activeInitial) params.set("initial", activeInitial);
    try {
      const response = await fetch(`/api/backend/catalog/breeds?${params.toString()}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`breed page ${response.status}`);
      const next = await response.json() as BreedPage;
      setItems((current) => [...current, ...next.items.filter((item) => !current.some((old) => old.id === item.id))]);
      setPage(next.page);
      setHasMore(next.hasMore);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [activeInitial, hasMore, initialPage.limit, loading, page, query]);

  const selectInitial = useCallback(async (nextInitial: string) => {
    if (nextInitial === activeInitial || loading) return;
    setLoading(true);
    setFailed(false);
    const params = new URLSearchParams({ page: "1", limit: String(initialPage.limit) });
    if (query) params.set("q", query);
    if (nextInitial) params.set("initial", nextInitial);
    try {
      const response = await fetch(`/api/backend/catalog/breeds?${params.toString()}`, { cache: "no-store" });
      if (!response.ok) throw new Error(`breed initial ${response.status}`);
      const next = await response.json() as BreedPage;
      setItems(next.items);
      setTotal(next.total);
      setPage(next.page);
      setHasMore(next.hasMore);
      setActiveInitial(nextInitial);
      const url = new URL(window.location.href);
      if (nextInitial) url.searchParams.set("initial", nextInitial); else url.searchParams.delete("initial");
      window.history.replaceState({}, "", `${url.pathname}${url.search}`);
    } catch {
      setFailed(true);
    } finally {
      setLoading(false);
    }
  }, [activeInitial, initialPage.limit, loading, query]);

  useEffect(() => {
    const target = sentinel.current;
    if (!target || !hasMore || loading || failed) return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting) return;
      observer.disconnect();
      void loadNext();
    }, { rootMargin: "300px 0px" });
    observer.observe(target);
    return () => observer.disconnect();
  }, [failed, hasMore, loadNext, loading]);

  useEffect(() => {
    if (!failed || !hasMore || loading) return;
    const retry = window.setTimeout(() => void loadNext(), 1500);
    return () => window.clearTimeout(retry);
  }, [failed, hasMore, loadNext, loading]);

  const groups = new Map<string, CatalogBreed[]>();
  for (const breed of items) {
    const letter = guideOf(breed).koreanInitial ?? "기타";
    groups.set(letter, [...(groups.get(letter) ?? []), breed]);
  }
  return <>
    <nav className="breed-initials" aria-label="견종 초성 필터">
      <button type="button" onClick={() => void selectInitial("")} className={!activeInitial ? "active" : ""}>전체</button>
      {initials.map((letter) => <button type="button" key={letter} onClick={() => void selectInitial(letter)} className={activeInitial === letter ? "active" : ""} aria-current={activeInitial === letter ? "page" : undefined}>{letter}</button>)}
    </nav>
    <div className="directory-intro" aria-live="polite"><div><b>{total}개 견종 가이드</b><p>{query ? `“${query}” 검색 결과` : activeInitial ? `${activeInitial} 초성 견종` : "AKC 공개 카탈로그 기준입니다."}</p></div>{(query || activeInitial) && <Link className="breed-filter-reset" href="/breeds">검색 조건 초기화</Link>}</div>
    {!items.length ? <div className="breed-empty"><h2>검색 결과가 없습니다</h2><p>한국어 또는 영문 견종 이름으로 다시 검색해보세요.</p><Link href="/breeds">전체 견종 보기</Link></div> : <>
    {[...groups].map(([letter, breeds]) => <section className="breed-initial-group" key={letter} aria-labelledby={`initial-${letter}`}>
      <h2 id={`initial-${letter}`}>{letter}</h2>
      <div className="breed-guide-grid">{breeds.map((breed) => {
        const guide = guideOf(breed);
        const illustration = getBreedIllustration(breed.slug);
        const safetyLabel = getBreedSafetyLabel(breed.slug);
        return <Link href={`/breeds/${breed.slug}`} className="breed-guide-card" key={breed.slug}>
          {illustration && <div className="breed-guide-image">
            <Image src={illustration} alt={`${breed.name} 색연필 일러스트`} fill sizes="(max-width: 520px) 100vw, (max-width: 850px) 50vw, 33vw" />
          </div>}
          <div className="breed-guide-copy"><small>{breed.englishName}</small><h3>{breed.name}</h3><p>{breed.summary}</p>
            <span className="breed-listing-count">{breed.listingCount}마리 가족 찾는 중</span>
            <div className="breed-meta"><span><Ruler />{guide.size?.split(" · ")[0] ?? "크기 확인"}</span><span><Clock3 />{guide.activity?.split(" · ")[0] ?? "활동량 확인"}</span></div>
            <div className="trait-row">
              {safetyLabel && <span className={`breed-safety-badge ${safetyLabel.kind}`} title={safetyLabel.description}>{safetyLabel.label}</span>}
              {(guide.traits ?? []).map((trait) => <span key={trait}>{trait}</span>)}
            </div>
            <b className="card-link">생활 가이드 보기 <ArrowRight /></b>
          </div>
        </Link>;
      })}</div>
    </section>)}
    </>}
    <div ref={sentinel} className="breed-load-sentinel" aria-live="polite">
      {loading && <><LoaderCircle aria-hidden="true" /> 다음 견종을 불러오는 중...</>}
      {failed && <span>불러오지 못했습니다. 잠시 후 자동으로 다시 시도합니다.</span>}
      {!hasMore && <span>모든 견종을 확인했습니다.</span>}
    </div>
  </>;
}
